import { useState, useEffect, useCallback, useRef } from "react";
import { X, MapPin, Building2, ExternalLink, Wifi, Code, Trash2, UserMinus } from "lucide-react";
import type { TheirStackJob, JobAssignee } from "../api/TheirStackJobsApi";
import { fetchJobAssignees } from "../api/TheirStackJobsApi";
import { STATUS_CHIP_SOLID, daysAgo } from "./shared";
import { useTheirStackJobs } from "../modal/TheirStackJobsContext";
import HighlightedText from "shared/HighlightedText";
import TimeAgoChip from "shared/TimeAgoChip";
import RemoteScopeChip from "shared/RemoteScopeChip";
import MultiAssignDropdown from "shared/MultiAssignDropdown";

interface Props {
  job: TheirStackJob;
  onClose: () => void;
}

export default function AdminJobDetailModal({ job, onClose }: Props) {
  const { techManagers, handleActivateJob, handleAssignJob, handleUnassignJob } = useTheirStackJobs();
  const [assignError, setAssignError] = useState<string | null>(null);
  const [assignSuccess, setAssignSuccess] = useState<string | null>(null);
  const [toggling, setToggling] = useState(false);

  const [assignees, setAssignees] = useState<JobAssignee[]>([]);
  const [assigneesLoading, setAssigneesLoading] = useState(true);
  const [assigneesError, setAssigneesError] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<number | null>(null);
  const [unassignError, setUnassignError] = useState<string | null>(null);

  // Guards against out-of-order responses if loadAssignees is triggered again
  // (e.g. rapid assign/unassign clicks) before an earlier fetch resolves.
  const assigneesRequestId = useRef(0);

  const loadAssignees = useCallback(async () => {
    const requestId = ++assigneesRequestId.current;
    setAssigneesLoading(true);
    setAssigneesError(null);
    try {
      const result = await fetchJobAssignees(job.id);
      if (requestId !== assigneesRequestId.current) return;
      setAssignees(result);
    } catch {
      if (requestId !== assigneesRequestId.current) return;
      setAssignees([]);
      setAssigneesError("Failed to load assignees. Please try again.");
    } finally {
      if (requestId === assigneesRequestId.current) setAssigneesLoading(false);
    }
  }, [job.id]);

  useEffect(() => { loadAssignees(); }, [loadAssignees]);

  async function doAssign(employeeIds: number[]) {
    setAssignError(null);
    setAssignSuccess(null);
    try {
      const result = await handleAssignJob(job.id, employeeIds);
      if (result.errors.length > 0) {
        const detail = result.errors
          .map((e) => `${techManagers.find((t) => t.id === e.employee_id)?.name ?? `#${e.employee_id}`}: ${e.detail}`)
          .join("; ");
        setAssignError(`Assigned ${result.assigned.length}, failed for ${result.errors.length} — ${detail}`);
      } else {
        setAssignSuccess(`Assigned ${employeeIds.length} ${employeeIds.length === 1 ? "TM" : "TMs"} successfully.`);
      }
    } catch (err: any) {
      // Don't also set assignError here — MultiAssignDropdown surfaces this same
      // message itself (via the rethrow below) so the two don't end up shown at once.
      throw new Error(err?.response?.data?.detail ?? "Failed to assign. Please try again.");
    } finally {
      await loadAssignees();
    }
  }

  async function doUnassign(employeeId: number) {
    setRemovingId(employeeId);
    setUnassignError(null);
    try {
      await handleUnassignJob(job.id, employeeId);
    } catch (err: any) {
      setUnassignError(err?.response?.data?.detail ?? "Failed to remove assignment. Please try again.");
      // Re-sync regardless of outcome — a failure here (e.g. 404) usually means
      // the assignment is already gone (removed elsewhere), so the list was stale.
    } finally {
      await loadAssignees();
      setRemovingId(null);
    }
  }

  async function toggleActive() {
    setToggling(true);
    try {
      await handleActivateJob(job.id, !job.is_active);
    } finally {
      setToggling(false);
    }
  }

  const description = job.description?.trim();

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-2xl max-h-[90vh] bg-surface border border-border rounded-2xl flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start gap-4 p-6 border-b border-border shrink-0">
          <div className="w-12 h-12 rounded-xl border border-border bg-surface-muted flex items-center justify-center shrink-0">
            <span className="text-lg font-bold text-primary font-inter">
              {job.company_name.charAt(0).toUpperCase()}
            </span>
          </div>

          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold text-foreground font-inter leading-snug">{job.job_title}</h2>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
              {job.date_posted && (
                <TimeAgoChip label={daysAgo(job.date_posted)} />
              )}
              <span className="flex items-center gap-1 text-xs text-muted-foreground font-inter">
                <Building2 className="w-3 h-3" />
                {job.company_name}
              </span>
              {job.location && (
                <span className="flex items-center gap-1 text-xs text-muted-foreground font-inter">
                  <MapPin className="w-3 h-3" />
                  {job.location}
                </span>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-1.5 mt-2">
              {job.seniority && (
                <span className="text-[11px] px-2 py-1 rounded-lg border bg-muted/50 text-foreground border-border/40 font-inter">
                  {job.seniority}
                </span>
              )}
              {job.employment_type && (
                <span className="text-[11px] px-2 py-1 rounded-lg border bg-muted/50 text-foreground border-border/40 font-inter">
                  {job.employment_type}
                </span>
              )}
              {job.remote && (
                <span className="flex items-center gap-0.5 text-[11px] px-2 py-1 rounded-lg border bg-emerald-500/10 text-emerald-400 border-emerald-500/20 font-inter">
                  <Wifi className="w-3 h-3" />
                  Remote
                </span>
              )}
              {job.hybrid && !job.remote && (
                <span className="text-[11px] px-2 py-1 rounded-lg border bg-sky-500/10 text-sky-400 border-sky-500/20 font-inter">
                  Hybrid
                </span>
              )}
              {job.stack_source && (
                <span className="flex items-center gap-0.5 text-[11px] px-2 py-1 rounded-lg border bg-muted/50 text-foreground border-border/40 font-inter">
                  <Code className="w-3 h-3" />
                  {job.stack_source}
                </span>
              )}
              {!job.is_active && (
                <span className="text-[11px] px-2 py-1 rounded-lg border bg-red-500/10 text-red-400 border-red-500/20 font-inter">
                  Deactivated
                </span>
              )}
            </div>
          </div>

          <RemoteScopeChip
            remoteScope={job.remote_scope}
            analysisJson={job.llm_analysis_metadata}
            className="shrink-0 self-start"
          />
          <button onClick={onClose} className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-surface-muted rounded-lg transition-colors shrink-0 self-start">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Assign to TM */}
        <div className="px-6 py-4 border-b border-border shrink-0">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground font-inter mb-2">
            Assigned To
          </p>
          {assigneesLoading ? (
            <p className="text-xs text-muted-foreground font-inter mb-3">Loading…</p>
          ) : assigneesError ? (
            <p className="text-xs text-red-400 font-inter mb-3">
              {assigneesError}{" "}
              <button onClick={loadAssignees} className="underline hover:text-red-300">Retry</button>
            </p>
          ) : assignees.length === 0 ? (
            <p className="text-xs text-muted-foreground font-inter italic mb-3">Not assigned to anyone yet.</p>
          ) : (
            <div className="flex flex-wrap items-center gap-1.5 mb-3">
              {assignees.map((a) => {
                const statusEntry = STATUS_CHIP_SOLID[a.status];
                return (
                  <span
                    key={a.employee_id}
                    className="flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-lg border bg-surface-muted border-border text-xs text-foreground font-inter"
                  >
                    {a.name}
                    {statusEntry && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded border font-inter font-medium ${statusEntry.className}`}>
                        {statusEntry.label}
                      </span>
                    )}
                    <button
                      onClick={() => doUnassign(a.employee_id)}
                      disabled={removingId === a.employee_id}
                      title="Remove assignment"
                      className="p-0.5 text-muted-foreground hover:text-destructive disabled:opacity-40 transition-colors"
                    >
                      <UserMinus className="w-3 h-3" />
                    </button>
                  </span>
                );
              })}
            </div>
          )}
          {unassignError && <p className="text-[11px] text-red-400 font-inter mb-3">{unassignError}</p>}

          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground font-inter mb-2">
            Assign to Technical Manager
          </p>
          <MultiAssignDropdown
            options={techManagers
              .filter((tm) => !assignees.some((a) => a.employee_id === tm.id))
              .map((tm) => ({
                id: tm.id,
                label: tm.tech_tags.length > 0 ? `${tm.name} (${tm.tech_tags.join(", ")})` : tm.name,
              }))}
            onConfirm={doAssign}
            placeholder="Select TMs…"
            confirmLabel={(n) => (n > 0 ? `Assign ${n}` : "Assign")}
            emptyMessage="All eligible TMs are already assigned."
          />
          {assignError && <p className="text-[11px] text-red-400 font-inter mt-1">{assignError}</p>}
          {assignSuccess && <p className="text-[11px] text-emerald-400 font-inter mt-1">{assignSuccess}</p>}
        </div>

        {/* Description */}
        <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
          {description ? (
            <div className="text-sm text-foreground font-inter leading-relaxed whitespace-pre-wrap">
              <HighlightedText text={description} />
            </div>
          ) : (
            <p className="text-sm text-muted-foreground font-inter italic">No description available.</p>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border shrink-0 flex items-center gap-3">
          <a
            href={job.job_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-inter font-medium rounded-xl transition-colors whitespace-nowrap"
          >
            <ExternalLink className="w-4 h-4" />
            View Job
          </a>
          <button
            onClick={toggleActive}
            disabled={toggling}
            className={`flex items-center gap-1.5 px-4 py-2 border text-sm font-inter font-medium rounded-xl transition-colors disabled:opacity-50 ${
              job.is_active
                ? "border-red-500/40 text-red-400 hover:bg-red-500/10"
                : "border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10"
            }`}
          >
            <Trash2 className="w-4 h-4" />
            {toggling ? "…" : job.is_active ? "Deactivate" : "Activate"}
          </button>
        </div>
      </div>
    </div>
  );
}
